/**
 * @fileoverview Deleting the signed-in administrator's own account (Settings →
 * You → Preferences): the web twin of the iPhone's DeleteAccountCard,
 * there because App Review guideline 5.1.1(v) asks it of every account the
 * app can create, and here so the two portals agree.
 *
 * `DELETE /org/auth/account` removes the account and every session it holds
 * and refuses the organization's only active administrator (400 last_admin),
 * so while the loaded admin list says that is the case the button is held
 * back with the reason. After a deletion the portal's `logout` forgets the
 * session and opens the sign-in page.
 */

import { useContext, useState } from "react";
import { toast } from "sonner";
import { OrgAuthContext } from "@/App";
import orgApi, { getOrgErrorMessage } from "@/lib/orgApi";
import { Button } from "@/components/studio/primitives";

export const DELETE_ACCOUNT_NOTE =
  "Deleting your account removes your administrator sign-in. The organization, its programs and its records stay.";
export const LAST_ADMIN_NOTE = "You're the only active administrator. Add another admin before deleting your account.";
export const DELETE_ACCOUNT_CONFIRM =
  "Delete your administrator account? This signs you out and cannot be undone. The organization, its programs and records stay.";

/**
 * @param {Object} props
 * @param {number|null} props.otherActiveAdmins - How many other active admins the
 *   organization has; 0 holds the button back; null while unknown.
 */
export default function OrgDeleteAccount({ otherActiveAdmins }) {
  const { logout } = useContext(OrgAuthContext) || {};
  const [deleting, setDeleting] = useState(false);
  const lastAdmin = otherActiveAdmins === 0;

  const remove = async () => {
    if (deleting || lastAdmin) return;
    if (!window.confirm(DELETE_ACCOUNT_CONFIRM)) return;
    setDeleting(true);
    try {
      await orgApi.delete("/org/auth/account");
      toast.success("Your account was deleted");
      if (logout) await logout();
      else window.location.href = "/org/login";
    } catch (err) {
      toast.error(getOrgErrorMessage(err, "Couldn't delete your account"));
      setDeleting(false);
    }
  };

  return (
    <div className="mt-6 flex max-w-sm flex-col gap-3 border-t border-[var(--studio-divider)] pt-5" data-testid="org-delete-account-card">
      <p className="font-sans text-[13px] text-[var(--text-tertiary)]" data-testid="org-delete-account-note">
        {lastAdmin ? LAST_ADMIN_NOTE : DELETE_ACCOUNT_NOTE}
      </p>
      <div>
        <Button
          type="button"
          variant="danger"
          onClick={remove}
          disabled={deleting || lastAdmin}
          icon={deleting ? "loader" : "trash"}
          data-testid="org-delete-account"
        >
          {deleting ? "Deleting…" : "Delete account"}
        </Button>
      </div>
    </div>
  );
}
